/* Homepage video feature. History drives GA's existing automatic page views;
 * interaction events below are deliberately NOT additional page_view events. */
(() => {
  'use strict';
  if (customElements.get('tds-video-hero')) return;
  // All instances share the original page title and a single modal owner.
  const pageTitle = document.title;
  const instances = new Set();
  let modalOwner = null;

  class VideoHero extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.slides = [...this.querySelectorAll('[data-slide]')];
      if (!this.slides.length) return;
      this.controller = new AbortController();
      this.video = this.querySelector('[data-hero-video]');
      this.stage = this.querySelector('[data-stage]');
      this.dialog = this.querySelector('[data-dialog]');
      this.player = this.querySelector('[data-player]');
      this.baseTitle = pageTitle;
      this.index = -1;
      this.visible = false;
      this.wheelDistance = 0;
      this.wheelLastAt = 0;
      this.wheelLockedUntil = 0;
      // The preview never starts by itself (TDS 2.0: no auto-playing motion). It stays on the
      // poster, and its source is not even fetched, until the visitor presses the play button.
      this.userPaused = true;
      this.listen(this, 'click', event => this.onClick(event));
      this.listen(this, 'wheel', event => this.onWheel(event), { passive: false });
      this.listen(this.stage, 'pointerdown', event => this.pointerDown(event));
      this.listen(this.stage, 'pointermove', event => this.pointerMove(event));
      this.listen(this.stage, 'pointerup', event => this.pointerUp(event));
      this.listen(this.stage, 'pointercancel', () => this.resetPointer());
      this.listen(this.stage, 'dragstart', event => event.preventDefault());
      this.listen(this.stage, 'keydown', event => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        this.select(this.index + (event.key === 'ArrowRight' ? 1 : -1), 'keyboard');
      });
      this.listen(this.dialog, 'cancel', event => { event.preventDefault(); this.requestClose(); });
      this.listen(this.dialog, 'click', event => {
        if (event.target !== this.dialog) return;
        const box = this.dialog.getBoundingClientRect();
        if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) this.requestClose();
      });
      this.listen(window, 'popstate', () => this.restoreUrl());
      this.listen(window, 'pageshow', () => { this.restoreUrl(); this.updatePlayback(); });
      this.listen(document, 'visibilitychange', () => this.updatePlayback());
      this.listen(this.video, 'loadedmetadata', () => this.seek());
      this.listen(this.video, 'play', () => this.updatePauseButton());
      this.listen(this.video, 'pause', () => this.updatePauseButton());
      this.listen(this, 'shopify:block:select', event => {
        const index = this.slides.findIndex(slide => slide.dataset.slideKey === event.detail.blockId);
        if (index >= 0) this.select(index);
      });
      this.observer = new IntersectionObserver(entries => {
        this.visible = entries[0].isIntersecting;
        this.updatePlayback();
      }, { threshold: 0.1 });
      this.observer.observe(this.video);
      instances.add(this);
      this.restoreUrl();
      this.updatePauseButton();
    }

    listen(target, type, handler, options = {}) {
      target.addEventListener(type, handler, { ...options, signal: this.controller.signal });
    }

    get tracking() { return this.dataset.urlTracking !== 'false' && !window.Shopify?.designMode; }
    get activeSlide() { return this.slides[this.index]; }
    get urlKey() { return this.dataset.urlKey || ''; }
    get slideParam() { return this.urlKey ? `hero_slide_${this.urlKey}` : 'hero_slide'; }

    ownsUrl(params) { return (params.get('hero_section') || '') === this.urlKey; }

    setUrlOwner(url) {
      if (this.urlKey) url.searchParams.set('hero_section', this.urlKey);
      else url.searchParams.delete('hero_section');
    }

    updateAllPlayback() {
      for (const instance of new Set([...instances, this])) instance.updatePlayback();
    }

    select(index, method) {
      index = (index + this.slides.length) % this.slides.length;
      if (index === this.index) return;
      // URL restoration of another instance must leave the owner's modal alone.
      if (method && modalOwner && modalOwner !== this) modalOwner.closeModal(false);
      if (this.dialog.open) this.closeModal();
      this.index = index;
      this.dataset.activeIndex = String(index);
      this.slides.forEach((slide, position) => {
        slide.hidden = position !== index;
        slide.inert = position !== index;
        slide.setAttribute('aria-hidden', String(position !== index));
      });
      this.querySelectorAll('[data-dot]').forEach((dot, position) => {
        dot.setAttribute('aria-current', position === index ? 'true' : 'false');
      });
      this.querySelector('[data-current]').textContent = String(index + 1).padStart(2, '0');
      this.video.poster = this.activeSlide.dataset.poster || '';
      this.querySelector('[data-media-open]')?.setAttribute('aria-label', `${this.activeSlide.dataset.title}の動画を開く`);
      this.seek();
      this.updatePlayback();
      if (method === 'keyboard') this.activeSlide.querySelector('[data-open]').focus({ preventScroll: true });
      if (method) {
        this.querySelector('[data-status]').textContent = `${index + 1} / ${this.slides.length}：${this.activeSlide.dataset.title}`;
        if (this.tracking) {
          const url = new URL(location.href);
          url.searchParams.set(this.slideParam, this.activeSlide.dataset.slideKey);
          this.setUrlOwner(url);
          url.searchParams.delete('hero_video');
          this.writeHistory(url, 'replaceState');
        }
        this.track('hero_slide_change', method);
      }
    }

    titleFor(modal) {
      return `${this.activeSlide.dataset.title}${modal ? '｜動画' : ''}｜${this.baseTitle}`;
    }

    writeHistory(url, operation, state = history.state) {
      if (url.href === location.href) return false;
      const oldTitle = document.title;
      document.title = this.titleFor(this.ownsUrl(url.searchParams) && url.searchParams.has('hero_video'));
      try { history[operation](state, '', url.href); return true; }
      catch (_) { document.title = oldTitle; return false; }
    }

    restoreUrl() {
      if (!this.tracking) { this.select(Math.max(0, this.index)); return; }
      const params = new URL(location.href).searchParams;
      const isOwner = this.ownsUrl(params);
      const modalKey = isOwner ? params.get('hero_video') : null;
      const modalIndex = this.slides.findIndex(slide => slide.dataset.slideKey === modalKey);
      const slideIndex = this.slides.findIndex(slide => slide.dataset.slideKey === params.get(this.slideParam));
      this.select(modalIndex >= 0 ? modalIndex : Math.max(0, slideIndex));
      if (modalIndex >= 0) this.openModal(false);
      else this.closeModal();
      if (isOwner) document.title = modalIndex >= 0 || slideIndex >= 0 ? this.titleFor(modalIndex >= 0) : this.baseTitle;
    }

    openModal(userInitiated = true) {
      if (this.dialog.open) return;
      const previousOwner = modalOwner;
      modalOwner = this;
      if (previousOwner && previousOwner !== this) previousOwner.closeModal(false);
      const slide = this.activeSlide;
      this.returnFocus = document.activeElement;
      this.querySelector('[data-modal-title]').textContent = slide.dataset.title;
      const body = this.querySelector('[data-modal-body]');
      body.replaceChildren(slide.querySelector('[data-modal-content]').content.cloneNode(true));
      const article = this.querySelector('[data-modal-article]');
      article.hidden = !slide.dataset.articleUrl;
      if (slide.dataset.articleUrl) article.href = slide.dataset.articleUrl;
      article.textContent = slide.dataset.articleLabel || 'この記事を読む ↗';
      const id = slide.dataset.youtubeId;
      const youtube = this.querySelector('[data-modal-youtube]');
      youtube.hidden = !/^[\w-]{11}$/.test(id || '');
      this.player.replaceChildren();
      if (!youtube.hidden) {
        youtube.href = `https://www.youtube.com/watch?v=${id}`;
        const iframe = document.createElement('iframe');
        iframe.src = `https://www.youtube-nocookie.com/embed/${id}?rel=0&playsinline=1`;
        iframe.title = slide.dataset.title;
        iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
        iframe.allowFullscreen = true;
        iframe.referrerPolicy = 'strict-origin-when-cross-origin';
        this.player.append(iframe);
      }
      this.lockScroll();
      this.dialog.showModal();
      this.querySelector('[data-close]').focus({ preventScroll: true });
      this.updateAllPlayback();
      if (userInitiated) {
        if (this.tracking) {
          const url = new URL(location.href);
          const from = url.href;
          url.searchParams.set(this.slideParam, slide.dataset.slideKey);
          this.setUrlOwner(url);
          url.searchParams.set('hero_video', slide.dataset.slideKey);
          const state = { ...(history.state || {}), tdsVideoHero: { section: this.dataset.sectionId, urlKey: this.urlKey, from } };
          this.writeHistory(url, 'pushState', state);
        }
        this.track('hero_video_open', 'click');
      }
    }

    requestClose() {
      if (!this.dialog.open) return;
      this.closeModal();
      this.track('hero_video_close', 'close');
      if (!this.tracking) return;
      const marker = history.state?.tdsVideoHero;
      if (marker?.section === this.dataset.sectionId && (marker.urlKey || '') === this.urlKey) {
        history.back();
      } else {
        // A directly opened/shared modal URL has no local parent entry.
        const url = new URL(location.href);
        url.searchParams.delete('hero_video');
        url.searchParams.set(this.slideParam, this.activeSlide.dataset.slideKey);
        this.setUrlOwner(url);
        this.writeHistory(url, 'replaceState');
      }
    }

    closeModal(restoreFocus = true) {
      if (!this.dialog?.open) return;
      this.dialog.close();
      if (modalOwner === this) modalOwner = null;
      this.player.replaceChildren(); // Tear down YouTube playback, including audio.
      this.unlockScroll();
      if (restoreFocus && this.returnFocus?.isConnected && !this.returnFocus.closest('[inert]')) this.returnFocus.focus({ preventScroll: true });
      this.updateAllPlayback();
    }

    lockScroll() {
      this.scrollY = window.scrollY;
      this.bodyStyles = {};
      ['position', 'top', 'left', 'right', 'width', 'overflow'].forEach(key => { this.bodyStyles[key] = document.body.style[key]; });
      Object.assign(document.body.style, { position: 'fixed', top: `-${this.scrollY}px`, left: '0', right: '0', width: '100%', overflow: 'hidden' });
    }

    unlockScroll() {
      if (!this.bodyStyles) return;
      Object.assign(document.body.style, this.bodyStyles);
      window.scrollTo({ top: this.scrollY, behavior: 'instant' });
      this.bodyStyles = null;
    }

    onClick(event) {
      if (Date.now() < (this.suppressClickUntil || 0) && this.stage.contains(event.target)) {
        event.preventDefault(); event.stopPropagation(); return;
      }
      if (event.target.closest('[data-close]')) return this.requestClose();
      if (event.target.closest('[data-prev]')) return this.select(this.index - 1, 'previous');
      if (event.target.closest('[data-next]')) return this.select(this.index + 1, 'next');
      const dot = event.target.closest('[data-dot]');
      if (dot) return this.select(Number(dot.dataset.index), 'dot');
      if (event.target.closest('[data-pause]')) {
        this.userPaused = !this.video.paused && !this.userPaused;
        this.updatePlayback(); this.updatePauseButton(); return;
      }
      if (event.target.closest('[data-slide-article], [data-modal-article]')) {
        this.track('hero_article_click', 'article');
        return; // Preserve normal link navigation; never also open the dialog.
      }
      if (event.target.closest('[data-open], [data-media-open]') || this.stage.contains(event.target)) this.openModal();
    }

    onWheel(event) {
      if (this.slides.length < 2 || this.dialog?.open) return;
      const rawX = Number(event.deltaX) || 0;
      const rawY = Number(event.deltaY) || 0;
      const shiftedWheel = event.shiftKey && Math.abs(rawY) > Math.abs(rawX);
      if (!shiftedWheel && (Math.abs(rawX) < 1 || Math.abs(rawX) <= Math.abs(rawY))) return;

      event.preventDefault();
      const unit = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? Math.max(this.clientWidth || 0, 320) : 1;
      const distance = (shiftedWheel ? rawY : rawX) * unit;
      const now = Date.now();
      if (now < (this.wheelLockedUntil || 0)) return;
      if (now - (this.wheelLastAt || 0) > 180 || Math.sign(distance) !== Math.sign(this.wheelDistance || distance)) {
        this.wheelDistance = 0;
      }
      this.wheelLastAt = now;
      this.wheelDistance += distance;
      if (Math.abs(this.wheelDistance) < 36) return;

      this.select(this.index + (this.wheelDistance > 0 ? 1 : -1), 'wheel');
      this.wheelDistance = 0;
      this.wheelLockedUntil = now + 420;
    }

    pointerDown(event) {
      if (!event.isPrimary || event.button !== 0 || this.dialog.open) return;
      this.pointer = { id: event.pointerId, x: event.clientX, y: event.clientY, axis: null };
    }

    pointerMove(event) {
      const pointer = this.pointer;
      if (!pointer || event.pointerId !== pointer.id) return;
      const dx = event.clientX - pointer.x, dy = event.clientY - pointer.y;
      if (!pointer.axis && Math.max(Math.abs(dx), Math.abs(dy)) > 8) {
        pointer.axis = Math.abs(dx) > Math.abs(dy) * 1.2 ? 'x' : 'y';
        if (pointer.axis === 'x') this.stage.setPointerCapture(event.pointerId);
      }
      if (pointer.axis === 'x') {
        event.preventDefault();
        this.stage.style.setProperty('--tvh-drag', `${Math.max(-45, Math.min(45, dx * 0.15))}px`);
        this.suppressClickUntil = Date.now() + 500;
      }
    }

    pointerUp(event) {
      const pointer = this.pointer;
      if (!pointer || event.pointerId !== pointer.id) return;
      const dx = event.clientX - pointer.x, dy = event.clientY - pointer.y;
      if (pointer.axis === 'x' && Math.abs(dx) >= 40 && Math.abs(dx) > Math.abs(dy) * 1.2) {
        this.select(this.index + (dx < 0 ? 1 : -1), 'swipe');
      }
      if (pointer.axis) this.suppressClickUntil = Date.now() + 500;
      this.resetPointer();
    }

    resetPointer() {
      if (this.pointer && this.stage.hasPointerCapture(this.pointer.id)) this.stage.releasePointerCapture(this.pointer.id);
      this.pointer = null;
      this.stage.style.removeProperty('--tvh-drag');
    }

    seek() {
      if (!this.activeSlide || !this.video.duration) return;
      const start = Math.max(0, Number(this.activeSlide.dataset.videoStart) || 0);
      if (start < this.video.duration) this.video.currentTime = start;
    }

    updatePlayback() {
      if (!this.activeSlide) return;
      const source = this.activeSlide.dataset.videoSrc;
      // Clear a previous video's decoded frame even if the new slide is paused.
      if (this.video.getAttribute('src') && this.video.getAttribute('src') !== source) {
        this.video.removeAttribute('src');
        this.video.load();
      }
      const shouldPlay = this.visible && !document.hidden && !modalOwner && !this.dialog.open && !this.userPaused;
      if (!shouldPlay) { this.video.pause(); return; }
      if (!source) return;
      if (this.video.getAttribute('src') !== source) {
        this.video.src = source;
        this.video.load();
      }
      this.video.muted = true;
      const playing = this.video.play();
      if (playing?.catch) playing.catch(() => { this.updatePauseButton(); });
    }

    updatePauseButton() {
      const button = this.querySelector('[data-pause]');
      const paused = this.userPaused || this.video.paused;
      button.setAttribute('aria-label', paused ? 'プレビュー動画を再生' : 'プレビュー動画を一時停止');
      button.dataset.paused = String(paused);
    }

    track(event, method) {
      if (window.Shopify?.designMode || this.dataset.measurement === 'off') return;
      window.dataLayer = window.dataLayer || [];
      const detail = {
        event, hero_slide_id: this.activeSlide.dataset.slideKey,
        hero_section_id: this.dataset.sectionId, hero_section_key: this.urlKey || 'editorial',
        hero_slide_index: this.index + 1, hero_slide_title: this.activeSlide.dataset.title,
        content_view_method: 'homepage_video', interaction_method: method,
        page_location: location.href, page_title: document.title,
        article_url: this.activeSlide.dataset.articleUrl || ''
      };
      window.dataLayer.push(detail);
      // Opt-in manual mode uses a separate event for a single GTM page-view tag.
      if (this.dataset.measurement === 'manual' && (event === 'hero_slide_change' || event === 'hero_video_open')) {
        window.dataLayer.push({ ...detail, event: 'homepage_video_view' });
      }
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.observer?.disconnect();
      instances.delete(this);
      this.visible = false;
      this.closeModal();
      this.video?.pause();
    }
  }
  customElements.define('tds-video-hero', VideoHero);
})();
